import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Phone, 
  Mail, 
  Calendar, 
  Plus,
  Eye,
  MessageSquare,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  ArrowLeft,
  X,
  FileText,
  Globe,
  Search,
  Filter,
  RefreshCw,
  Building2,
  UserCheck,
  ArrowRight,
  MapPin,
  Laptop,
  Bell,
  Edit3
} from 'lucide-react';
import { getAllApplications } from '../../services/moderatorService';
import { interviewService, Interview } from '../../services/interviewService';
import { useAuthStore } from '../../stores/authStore';
import { JobApplication } from '../../types';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import AddContactModal from './AddContactModal';

const AllApplicationsPage: React.FC = () => {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [filteredApplications, setFilteredApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedApplication, setSelectedApplication] = useState<JobApplication | null>(null);
  const [showContactModal, setShowContactModal] = useState(false);
  const [candidateFilter, setCandidateFilter] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [interviewsMap, setInterviewsMap] = useState<Record<string, Interview[]>>({});

  useEffect(() => {
    loadData();
  }, []);

  // Filtra e ordina le candidature
  useEffect(() => {
    let filtered = applications;
    
    // Filtra per nome candidato
    if (candidateFilter.trim()) {
      filtered = applications.filter(application => {
        const candidateName = application.userName || '';
        const candidateEmail = application.userEmail || '';
        const filterLower = candidateFilter.toLowerCase();
        
        return candidateName.toLowerCase().includes(filterLower) || 
               candidateEmail.toLowerCase().includes(filterLower);
      });
    }
    
    // Ordina per data (più recenti prima) - migliorato
    filtered = filtered.sort((a, b) => {
      if (!a.appliedDate && !b.appliedDate) return 0;
      if (!a.appliedDate) return 1; // a va dopo se non ha data
      if (!b.appliedDate) return -1; // b va dopo se non ha data
      
      let dateA: number;
      let dateB: number;
      
      // Gestione Firestore Timestamp
      if (a.appliedDate?.toMillis) {
        dateA = a.appliedDate.toMillis();
      } else if (typeof a.appliedDate === 'number') {
        dateA = a.appliedDate;
      } else if (a.appliedDate?.toDate) {
        dateA = a.appliedDate.toDate().getTime();
      } else {
        dateA = 0;
      }
      
      if (b.appliedDate?.toMillis) {
        dateB = b.appliedDate.toMillis();
      } else if (typeof b.appliedDate === 'number') {
        dateB = b.appliedDate;
      } else if (b.appliedDate?.toDate) {
        dateB = b.appliedDate.toDate().getTime();
      } else {
        dateB = 0;
      }
      
      return dateB - dateA; // Più recenti prima
    });
    
    setFilteredApplications(filtered);
  }, [applications, candidateFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const allApplications = await getAllApplications();
      setApplications(allApplications);
      
      // Carica i colloqui per ogni candidatura
      await loadInterviewsForApplications(allApplications);
    } catch (error) {
      console.error('Error loading applications:', error);
      setError('Errore nel caricamento delle candidature');
    } finally {
      setLoading(false);
    }
  };

  const loadInterviewsForApplications = async (applications: JobApplication[]) => {
    try {
      const interviewsData: Record<string, Interview[]> = {};
      
      // Carica i colloqui per ogni candidatura
      for (const application of applications) {
        if (application.userId && application.companyName && application.position) {
          const interviews = await interviewService.getActiveInterviewsForApplication(
            application.userId,
            application.companyName,
            application.position
          );
          interviewsData[application.id] = interviews;
        }
      }
      
      setInterviewsMap(interviewsData);
    } catch (error) {
      console.error('Error loading interviews:', error);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadData();
    setIsRefreshing(false);
  };

  const closeDetailModal = () => {
    setSelectedApplication(null);
  };

  const closeContactModal = () => {
    setShowContactModal(false);
    setSelectedApplication(null);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'In attesa': return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'Colloquio programmato': return <Calendar className="w-4 h-4 text-blue-500" />;
      case 'Accettato': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Rifiutato': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <AlertCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In attesa': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Colloquio programmato': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'Accettato': return 'bg-green-100 text-green-800 border-green-200';
      case 'Rifiutato': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getContactMethodIcon = (method: string) => {
    switch (method) {
      case 'Email': return <Mail className="w-4 h-4" />;
      case 'Telefono': return <Phone className="w-4 h-4" />;
      case 'LinkedIn': return <MessageSquare className="w-4 h-4" />;
      case 'Lettera': return <FileText className="w-4 h-4" />;
      case 'Form web': return <Globe className="w-4 h-4" />;
      default: return <MessageSquare className="w-4 h-4" />;
    }
  };

  const renderInterviewDetails = (application: JobApplication) => {
    const interviews = interviewsMap[application.id] || [];
    
    if (interviews.length === 0) {
      return null;
    }

    const interview = interviews[0]; // Prendi il primo colloquio attivo
    
    return (
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-200 mt-4 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></div>
            <h4 className="font-bold text-blue-900 text-sm">🗓️ Colloquio Programmatto</h4>
          </div>
          <div className="flex items-center gap-2">
            {interview.interviewType === 'online' ? (
              <div className="flex items-center gap-1 bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 px-3 py-1 rounded-full text-xs font-medium shadow-sm">
                <Laptop className="w-3 h-3" />
                <span>Online</span>
              </div>
            ) : (
              <div className="flex items-center gap-1 bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium shadow-sm">
                <MapPin className="w-3 h-3" />
                <span>In presenza</span>
              </div>
            )}
          </div>
        </div>

        <div className="space-y-3">
          {/* Orario */}
          <div className="flex items-center gap-3 text-sm bg-white rounded-lg p-3 shadow-sm">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <Clock className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <span className="font-semibold text-gray-900">Orario:</span>
              <span className="text-gray-700 ml-2">
                {format(interview.interviewDate.toDate(), 'dd/MM/yyyy', { locale: it })} alle {interview.interviewTime}
              </span>
            </div>
          </div>

          {/* Posizione */}
          {interview.interviewLocation && (
            <div className="flex items-center gap-3 text-sm bg-white rounded-lg p-3 shadow-sm">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <MapPin className="w-4 h-4 text-red-600" />
              </div>
              <div>
                <span className="font-semibold text-gray-900">Indirizzo:</span>
                <span className="text-gray-700 ml-2">{interview.interviewLocation}</span>
              </div>
            </div>
          )}

          {/* Contatti */}
          {(interview.contactPerson || interview.contactEmail || interview.contactPhone) && (
            <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-lg p-4 border border-gray-200">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                  <Users className="w-3 h-3 text-gray-600" />
                </div>
                <span className="font-semibold text-gray-900 text-sm">📞 Contatti</span>
              </div>
              <div className="space-y-2">
                {interview.contactPerson && (
                  <div className="flex items-center gap-2 text-xs bg-white rounded-lg p-2">
                    <Users className="w-3 h-3 text-gray-500" />
                    <span className="text-gray-700 font-medium">{interview.contactPerson}</span>
                    {interview.contactRole && (
                      <span className="text-gray-500 bg-gray-100 px-2 py-1 rounded-full text-xs">({interview.contactRole})</span>
                    )}
                  </div>
                )}
                {interview.contactEmail && (
                  <div className="flex items-center gap-2 text-xs bg-white rounded-lg p-2">
                    <Mail className="w-3 h-3 text-gray-500" />
                    <span className="text-gray-700">{interview.contactEmail}</span>
                  </div>
                )}
                {interview.contactPhone && (
                  <div className="flex items-center gap-2 text-xs bg-white rounded-lg p-2">
                    <Phone className="w-3 h-3 text-gray-500" />
                    <span className="text-gray-700">{interview.contactPhone}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Note */}
          {interview.notes && (
            <div className="bg-gradient-to-r from-yellow-50 to-amber-50 rounded-lg p-4 border border-yellow-200">
              <div className="flex items-center gap-2 mb-2">
                <div className="w-6 h-6 bg-yellow-200 rounded-full flex items-center justify-center">
                  <Edit3 className="w-3 h-3 text-yellow-700" />
                </div>
                <span className="font-semibold text-yellow-800 text-sm">📝 Note</span>
              </div>
              <p className="text-yellow-900 text-sm bg-white rounded-lg p-3 border border-yellow-100">{interview.notes}</p>
            </div>
          )}

          {/* Reminder */}
          <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-lg p-2 border border-green-200">
            <Bell className="w-3 h-3" />
            <span className="font-medium">🔔 Reminder attivo</span>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Caricamento...</h3>
          <p className="text-gray-600">Stiamo caricando le candidature</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center max-w-sm w-full">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Errore</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={loadData}
            className="w-full bg-blue-600 text-white rounded-xl py-3 px-4 font-medium hover:bg-blue-700 transition-colors"
          >
            Riprova
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 lg:p-4 space-y-4 lg:space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 lg:gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 lg:gap-3 mb-2">
              <button
                onClick={() => navigate('/dashboard')}
                className="p-1.5 lg:p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg lg:rounded-xl transition-colors"
              >
                <ArrowLeft className="w-4 h-4 lg:w-5 lg:h-5" />
              </button>
              <div className="p-1.5 lg:p-2 bg-blue-100 rounded-lg lg:rounded-xl">
                <Users className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-gray-900">Tutte le Candidature</h1>
                <p className="text-sm lg:text-base text-gray-600">Gestione completa candidature</p>
              </div>
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2 lg:gap-3">
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center px-3 py-2 lg:px-4 lg:py-2 bg-gray-100 text-gray-700 rounded-lg lg:rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm lg:text-base"
            >
              <RefreshCw className={`w-3 h-3 lg:w-4 lg:h-4 mr-1 lg:mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
          </div>
        </div>
      </div>

      {/* Search and Stats */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg p-4 lg:p-6">
        <div className="space-y-4 lg:space-y-6">
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 lg:pl-4 flex items-center pointer-events-none">
              <Search className="h-4 h-4 lg:h-5 lg:w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Cerca per nome candidato..."
              value={candidateFilter}
              onChange={(e) => setCandidateFilter(e.target.value)}
              className="block w-full pl-10 lg:pl-12 pr-4 py-2 lg:py-3 border border-gray-300 rounded-lg lg:rounded-xl leading-5 bg-gray-50 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent focus:bg-white transition-all text-sm lg:text-base"
            />
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <div className="bg-blue-50 rounded-lg lg:rounded-xl p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Candidature Trovate</p>
                  <p className="text-xl lg:text-2xl font-bold text-gray-900">{filteredApplications.length}</p>
                </div>
                <div className="p-2 lg:p-3 bg-blue-100 rounded-lg lg:rounded-xl">
                  <Search className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-green-50 rounded-lg lg:rounded-xl p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Totale</p>
                  <p className="text-xl lg:text-2xl font-bold text-gray-900">{applications.length}</p>
                </div>
                <div className="p-2 lg:p-3 bg-green-100 rounded-lg lg:rounded-xl">
                  <Users className="w-4 h-4 lg:w-5 lg:h-5 text-green-600" />
                </div>
              </div>
            </div>

            <div className="bg-yellow-50 rounded-lg lg:rounded-xl p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">In Attesa</p>
                  <p className="text-xl lg:text-2xl font-bold text-gray-900">
                    {applications.filter(app => app.status === 'In attesa').length}
                  </p>
                </div>
                <div className="p-2 lg:p-3 bg-yellow-100 rounded-lg lg:rounded-xl">
                  <Clock className="w-4 h-4 lg:w-5 lg:h-5 text-yellow-600" />
                </div>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg lg:rounded-xl p-3 lg:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs lg:text-sm font-medium text-gray-600 mb-1">Colloqui</p>
                  <p className="text-xl lg:text-2xl font-bold text-gray-900">
                    {applications.filter(app => app.status === 'Colloquio programmato').length}
                  </p>
                </div>
                <div className="p-2 lg:p-3 bg-purple-100 rounded-lg lg:rounded-xl">
                  <Calendar className="w-4 h-4 lg:w-5 lg:h-5 text-purple-600" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Applications List */}
      <div className="bg-white rounded-xl lg:rounded-2xl shadow-lg">
        <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-lg lg:text-xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="w-4 h-4 lg:w-5 lg:h-5 text-blue-600" />
            Candidature ({filteredApplications.length})
          </h2>
          <div className="text-sm lg:text-base text-gray-600">
            Ordinate per data (più recenti)
          </div>
        </div>
        
        {/* Desktop: Griglia 2x2 */}
        <div className="hidden lg:block p-4 lg:p-6">
          {filteredApplications.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-gray-500">
                <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <p className="text-lg font-medium">
                  {candidateFilter ? 'Nessuna candidatura trovata' : 'Nessuna candidatura'}
                </p>
                <p className="text-sm">
                  {candidateFilter ? 'Prova a modificare i criteri di ricerca' : 'Non ci sono ancora candidature nel sistema'}
                </p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 lg:gap-6">
              {filteredApplications.map((application) => (
                <div key={application.id} className="bg-gray-50 rounded-xl p-4 lg:p-6 border border-gray-200 hover:shadow-lg transition-all duration-200 hover:border-gray-300">
                  {/* Header Card */}
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <div className="p-2 bg-blue-100 rounded-lg mr-3">
                          <Building2 className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900 text-lg">{application.companyName}</h3>
                          <p className="text-blue-600 font-medium text-sm">{application.position}</p>
                        </div>
                      </div>
                      <div className="flex items-center text-sm text-gray-600 mb-1">
                        <UserCheck className="w-4 h-4 mr-2 text-gray-500" />
                        <span className="font-medium">{application.userName || 'N/A'}</span>
                      </div>
                      <p className="text-sm text-gray-500">{application.userEmail || 'N/A'}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => setSelectedApplication(application)}
                        className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                        title="Visualizza dettagli"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedApplication(application);
                          setShowContactModal(true);
                        }}
                        className="p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                        title="Aggiungi contatto"
                      >
                        <Plus className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Dettagli Card */}
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-4 h-4 text-gray-500" />
                          <span className="text-sm font-medium text-gray-700">Data</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900">
                          {application.appliedDate ? format(application.appliedDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                        </p>
                      </div>

                      <div className="bg-white rounded-lg p-3 border border-gray-200">
                        <div className="flex items-center gap-2 mb-1">
                          {getContactMethodIcon(application.contactMethod)}
                          <span className="text-sm font-medium text-gray-700">Metodo</span>
                        </div>
                        <p className="text-sm font-semibold text-gray-900 capitalize">{application.contactMethod}</p>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Stato:</span>
                      <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full border ${getStatusColor(application.status)}`}>
                        {getStatusIcon(application.status)}
                        <span className="text-sm font-medium">{application.status}</span>
                      </div>
                    </div>

                    {/* Notes */}
                    {application.notes && (
                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                        <span className="text-sm font-medium text-blue-700 block mb-1">Note:</span>
                        <p className="text-sm text-blue-900">{application.notes}</p>
                      </div>
                    )}

                    {/* Interview Details */}
                    {renderInterviewDetails(application)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Mobile: Cards */}
        <div className="lg:hidden p-3 lg:p-4 space-y-3 lg:space-y-4">
          {filteredApplications.length === 0 ? (
            <div className="text-center py-6 lg:py-8">
              <div className="text-gray-500">
                <Users className="w-12 h-12 lg:w-16 lg:h-16 mx-auto mb-3 lg:mb-4 text-gray-300" />
                <p className="text-base lg:text-lg font-medium">
                  {candidateFilter ? 'Nessuna candidatura trovata' : 'Nessuna candidatura'}
                </p>
                <p className="text-xs lg:text-sm">
                  {candidateFilter ? 'Prova a modificare i criteri di ricerca' : 'Non ci sono ancora candidature nel sistema'}
                </p>
              </div>
            </div>
          ) : (
            filteredApplications.map((application) => (
              <div key={application.id} className="bg-gray-50 rounded-lg lg:rounded-xl p-3 lg:p-4 border border-gray-200 hover:shadow-md transition-shadow">
                {/* Header Card */}
                <div className="flex justify-between items-start mb-3 lg:mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-1 lg:mb-2">
                      <div className="p-1 bg-blue-100 rounded mr-2">
                        <Building2 className="w-3 h-3 text-blue-600" />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-base lg:text-lg">{application.companyName}</h3>
                    </div>
                    <p className="text-xs lg:text-sm text-gray-600 mb-1">{application.position}</p>
                    <div className="flex items-center text-xs lg:text-sm text-gray-600">
                      <UserCheck className="w-3 h-3 lg:w-4 lg:h-4 mr-1" />
                      <span className="font-medium">{application.userName || 'N/A'}</span>
                    </div>
                    <p className="text-xs text-gray-500">{application.userEmail || 'N/A'}</p>
                  </div>
                  <div className="flex space-x-1">
                    <button
                      onClick={() => setSelectedApplication(application)}
                      className="p-1.5 lg:p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
                      title="Visualizza dettagli"
                    >
                      <Eye className="w-3 h-3 lg:w-4 lg:h-4" />
                    </button>
                    <button
                      onClick={() => {
                        setSelectedApplication(application);
                        setShowContactModal(true);
                      }}
                      className="p-1.5 lg:p-2 text-green-600 hover:text-green-900 hover:bg-green-50 rounded-lg transition-colors"
                      title="Aggiungi contatto"
                    >
                      <Plus className="w-3 h-3 lg:w-4 lg:h-4" />
                    </button>
                  </div>
                </div>

                {/* Dettagli Card */}
                <div className="space-y-1.5 lg:space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-600">Data Candidatura:</span>
                    <span className="text-xs lg:text-sm font-medium text-gray-900">
                      {application.appliedDate ? format(application.appliedDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-600">Stato:</span>
                    <div className="flex items-center">
                      {getStatusIcon(application.status)}
                      <span className="ml-1 text-xs lg:text-sm font-medium text-gray-900">{application.status}</span>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-xs lg:text-sm text-gray-600">Metodo:</span>
                    <div className="flex items-center">
                      <MessageSquare className="w-3 h-3 lg:w-4 lg:h-4" />
                      <span className="ml-1 text-xs lg:text-sm font-medium text-gray-900 capitalize">{application.contactMethod}</span>
                    </div>
                  </div>

                  {application.notes && (
                    <div className="mt-2 lg:mt-3 pt-2 lg:pt-3 border-t border-gray-200">
                      <span className="text-xs lg:text-sm text-gray-600 block mb-1">Note:</span>
                      <p className="text-xs lg:text-sm text-gray-900 bg-white p-2 rounded-lg border">{application.notes}</p>
                    </div>
                  )}

                  {/* Interview Details Mobile */}
                  {renderInterviewDetails(application)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal Dettagli Candidatura */}
      {selectedApplication && !showContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 lg:p-4">
          {/* Mobile: Fullscreen */}
          <div className="lg:hidden fixed inset-0 bg-white flex flex-col">
            {/* Header Mobile */}
            <div className="flex justify-between items-center p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
              <h3 className="text-lg font-bold text-gray-900">Dettagli Candidatura</h3>
              <button
                onClick={closeDetailModal}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content Mobile */}
            <div className="flex-1 overflow-y-auto p-4 pb-32">
              <div className="space-y-4">
                <div className="grid grid-cols-1 gap-3">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Candidato</label>
                    <p className="text-sm text-gray-900">{selectedApplication.userName || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                    <p className="text-sm text-gray-900">{selectedApplication.userEmail || 'N/A'}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Azienda</label>
                    <p className="text-sm text-gray-900">{selectedApplication.companyName}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Posizione</label>
                    <p className="text-sm text-gray-900">{selectedApplication.position}</p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Data Candidatura</label>
                    <p className="text-sm text-gray-900">
                      {selectedApplication.appliedDate ? format(selectedApplication.appliedDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                    </p>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Stato</label>
                    <div className="flex items-center">
                      {getStatusIcon(selectedApplication.status)}
                      <span className="ml-2 text-sm text-gray-900">{selectedApplication.status}</span>
                    </div>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Metodo Contatto</label>
                    <div className="flex items-center">
                      <MessageSquare className="w-4 h-4 mr-2" />
                      <span className="text-sm text-gray-900 capitalize">{selectedApplication.contactMethod}</span>
                    </div>
                  </div>
                </div>
                
                {selectedApplication.notes && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
                    <p className="text-sm text-gray-900">{selectedApplication.notes}</p>
                  </div>
                )}
                
                {selectedApplication.companyDetails && (
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Dettagli Azienda</label>
                    <div className="space-y-2">
                      {selectedApplication.companyDetails.address && (
                        <p className="text-sm text-gray-900"><strong>Indirizzo:</strong> {selectedApplication.companyDetails.address}</p>
                      )}
                      {selectedApplication.companyDetails.phone && (
                        <p className="text-sm text-gray-900"><strong>Telefono:</strong> {selectedApplication.companyDetails.phone}</p>
                      )}
                      {selectedApplication.companyDetails.website && (
                        <p className="text-sm text-gray-900"><strong>Sito Web:</strong> {selectedApplication.companyDetails.website}</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Footer Mobile con padding per navbar */}
            <div className="bg-white border-t border-gray-200 p-4 pb-12">
              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowContactModal(true);
                  }}
                  className="flex-1 bg-green-600 text-white rounded-lg py-3 px-4 font-medium hover:bg-green-700 transition-colors"
                >
                  Aggiungi Contatto
                </button>
                <button
                  onClick={closeDetailModal}
                  className="flex-1 bg-gray-300 text-gray-700 rounded-lg py-3 px-4 font-medium hover:bg-gray-400 transition-colors"
                >
                  Chiudi
                </button>
              </div>
            </div>
          </div>

          {/* Desktop: Modal tradizionale */}
          <div className="hidden lg:block bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">Dettagli Candidatura</h3>
              <button
                onClick={closeDetailModal}
                className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Candidato</label>
                  <p className="text-sm text-gray-900">{selectedApplication.userName || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <p className="text-sm text-gray-900">{selectedApplication.userEmail || 'N/A'}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Azienda</label>
                  <p className="text-sm text-gray-900">{selectedApplication.companyName}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Posizione</label>
                  <p className="text-sm text-gray-900">{selectedApplication.position}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Data Candidatura</label>
                  <p className="text-sm text-gray-900">
                    {selectedApplication.appliedDate ? format(selectedApplication.appliedDate.toDate(), 'dd/MM/yyyy', { locale: it }) : 'N/A'}
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Stato</label>
                  <div className="flex items-center">
                    {getStatusIcon(selectedApplication.status)}
                    <span className="ml-2 text-sm text-gray-900">{selectedApplication.status}</span>
                  </div>
                </div>
                <div className="bg-gray-50 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Metodo Contatto</label>
                  <div className="flex items-center">
                    <MessageSquare className="w-4 h-4 mr-2" />
                    <span className="text-sm text-gray-900 capitalize">{selectedApplication.contactMethod}</span>
                  </div>
                </div>
              </div>
              
              {selectedApplication.notes && (
                <div className="bg-gray-50 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Note</label>
                  <p className="text-sm text-gray-900">{selectedApplication.notes}</p>
                </div>
              )}
              
              {selectedApplication.companyDetails && (
                <div className="bg-gray-50 p-4 rounded-xl">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Dettagli Azienda</label>
                  <div className="space-y-2">
                    {selectedApplication.companyDetails.address && (
                      <p className="text-sm text-gray-900"><strong>Indirizzo:</strong> {selectedApplication.companyDetails.address}</p>
                    )}
                    {selectedApplication.companyDetails.phone && (
                      <p className="text-sm text-gray-900"><strong>Telefono:</strong> {selectedApplication.companyDetails.phone}</p>
                    )}
                    {selectedApplication.companyDetails.website && (
                      <p className="text-sm text-gray-900"><strong>Sito Web:</strong> {selectedApplication.companyDetails.website}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => {
                  setShowContactModal(true);
                }}
                className="px-6 py-3 bg-green-600 text-white rounded-xl hover:bg-green-700 transition-colors font-medium"
              >
                Aggiungi Contatto
              </button>
              <button
                onClick={closeDetailModal}
                className="px-6 py-3 bg-gray-300 text-gray-700 rounded-xl hover:bg-gray-400 transition-colors font-medium"
              >
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Aggiungi Contatto */}
      {showContactModal && selectedApplication && (
        <AddContactModal
          isOpen={showContactModal}
          onClose={closeContactModal}
          application={selectedApplication}
          onContactAdded={loadData}
        />
      )}
    </div>
  );
};

export default AllApplicationsPage; 